import {Component, EventEmitter, Input, Output} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {MatFormField, MatInput} from '@angular/material/input';
import {MatRadioButton, MatRadioGroup} from '@angular/material/radio';
import {MatIcon} from '@angular/material/icon';
import {INPUT_LIMITS} from '../../../shared/constants/input-limits';

export type SaveConflictMode = 'update' | 'saveAs' | 'skip';

/**
 * Drei Situationen, in denen gefragt werden muss, wie gespeichert wird:
 *
 * - `conflict`: Das Backend hat den Namen als belegt gemeldet (409). Ohne die Rueckfrage
 *   ueberschriebe der Speichervorgang eine fremde Vorlage.
 * - `changedOnExport`: Eine geladene Vorlage wurde geaendert und soll exportiert werden.
 * - `changedOnClose`: Dasselbe beim Verlassen des Exportmodus.
 *
 * Die beiden letzten haben eine dritte Option, weil die Aenderungen dort auch folgenlos
 * bleiben duerfen — einmalig exportieren bzw. verwerfen.
 */
export type SaveDialogVariant = 'conflict' | 'changedOnExport' | 'changedOnClose';

const KEY_PREFIX: Record<SaveDialogVariant, string> = {
  conflict: 'export.template.exists',
  changedOnExport: 'export.template.changed',
  changedOnClose: 'export.template.closing',
};

let nextInstanceId = 0;

@Component({
  selector: 'app-twopager-save-dialog',
  imports: [
    FormsModule,
    TranslateModule,
    MatFormField,
    MatInput,
    MatRadioGroup,
    MatRadioButton,
    MatIcon
  ],
  templateUrl: './twopager-save-dialog.component.html',
  styleUrl: './twopager-save-dialog.component.scss',
  standalone: true
})
export class TwopagerSaveDialogComponent {
  private readonly instanceId = nextInstanceId++;
  readonly nameFieldId = `twopager-save-dialog-name-${this.instanceId}`;
  readonly titleId = `twopager-save-dialog-title-${this.instanceId}`;

  /**
   * Gesetzt wird von aussen: der Parent schliesst den Dialog erst, wenn er die Entscheidung
   * annimmt. Beim Schliessen faellt die Auswahl auf den Ausgangszustand zurueck, damit der
   * naechste Aufruf nicht mit dem alten Namen aufgeht.
   */
  @Input()
  set visible(value: boolean) {
    this.isVisible = value;
    if (!value) {
      this.chosenMode = null;
      this.newName = '';
      this.nameTouched = false;
    }
  }

  get visible(): boolean {
    return this.isVisible;
  }

  private isVisible = false;

  @Input() variant: SaveDialogVariant = 'conflict';
  /** Name der Vorlage, um die es geht — die belegte bzw. die geladene. */
  @Input() conflictingName = '';
  /** Bereits vergebene Vorlagennamen, damit der neue Name nicht erneut kollidiert. */
  @Input() existingNames: string[] = [];

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() updateExisting = new EventEmitter<void>();
  @Output() saveAsNew = new EventEmitter<string>();
  @Output() skipSave = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  /** null, solange der Nutzer nicht selbst gewaehlt hat — dann gilt `defaultMode`. */
  private chosenMode: SaveConflictMode | null = null;
  newName = '';
  /** Erst nach der ersten Eingabe wird der leere Name als Fehler *angezeigt* — siehe displayedNameErrorKey. */
  nameTouched = false;

  protected get LIMITS() { return INPUT_LIMITS; }

  /** Nur die Varianten einer geaenderten Vorlage bieten an, gar nicht zu speichern. */
  get isLoadedTemplate(): boolean {
    return this.variant !== 'conflict';
  }

  /**
   * Vorbelegung der Optionen. Beim Namenskonflikt gehoert die vorhandene Vorlage jemand
   * anderem — stuende dort "aktualisieren" vorgewaehlt, ueberschriebe ein Return genau das,
   * was die Rueckfrage verhindern soll. Bei einer geaenderten, selbst geladenen Vorlage ist
   * das Aktualisieren dagegen der erwartete Weg.
   *
   * Als Getter und nicht als Zuweisung beim Oeffnen, damit die Vorbelegung nicht davon
   * abhaengt, in welcher Reihenfolge der Parent `variant` und `visible` setzt.
   */
  private get defaultMode(): SaveConflictMode {
    return this.variant === 'conflict' ? 'saveAs' : 'update';
  }

  get mode(): SaveConflictMode {
    return this.chosenMode ?? this.defaultMode;
  }

  set mode(value: SaveConflictMode) {
    this.chosenMode = value;
  }

  private get prefix(): string {
    return KEY_PREFIX[this.variant];
  }

  get titleKey(): string {
    return `${this.prefix}.title`;
  }

  get textKey(): string {
    return `${this.prefix}.text`;
  }

  get updateKey(): string {
    return this.isLoadedTemplate ? `${this.prefix}.overwrite` : `${this.prefix}.update`;
  }

  get skipKey(): string {
    return `${this.prefix}.skip`;
  }

  get saveText(): string {
    return this.isLoadedTemplate ? `${this.prefix}.confirm` : 'general.save';
  }

  get trimmedNewName(): string {
    return this.newName?.trim() ?? '';
  }

  get nameErrorKey(): string | null {
    if (this.mode !== 'saveAs') return null;
    if (!this.trimmedNewName) return 'export.template.name.required';
    if (this.trimmedNewName.length > INPUT_LIMITS.EXPORT_CONFIG_NAME) return 'export.template.name.tooLong';
    if (this.isTaken(this.trimmedNewName)) return 'export.template.name.duplicate';
    return null;
  }

  /**
   * Was im Dialog steht. Der leere Name ist ein Fehler, aber kein Vorwurf: beim Umschalten auf
   * "unter neuem Namen speichern" ist das Feld zwangslaeufig leer, der Nutzer hatte noch keine
   * Gelegenheit zu tippen. Dass noch nichts abzuschicken ist, sagt bis dahin der gesperrte
   * Speichern-Knopf (`canSubmit` liest weiter die ungefilterte Regel).
   */
  get displayedNameErrorKey(): string | null {
    const key = this.nameErrorKey;
    if (key === 'export.template.name.required' && !this.nameTouched) return null;
    return key;
  }

  get canSubmit(): boolean {
    return this.nameErrorKey === null;
  }

  private isTaken(name: string): boolean {
    return (this.existingNames ?? []).some(existing => existing?.trim().toLowerCase() === name.toLowerCase());
  }

  /**
   * Der Dialog schliesst sich hier bewusst nicht selbst: ob die Entscheidung durchgeht, weiss
   * nur der Parent — er lehnt sie z. B. bei unbewerteten Faehigkeiten ab. Wird sie abgelehnt,
   * bleibt der Dialog mit der getroffenen Auswahl stehen, statt den Nutzer mit einem
   * Fehler-Toast im halb gespeicherten Exportmodus zurueckzulassen.
   */
  onSubmit(): void {
    if (!this.canSubmit) return;
    if (this.mode === 'update') {
      this.updateExisting.emit();
    } else if (this.mode === 'saveAs') {
      this.saveAsNew.emit(this.trimmedNewName);
    } else {
      this.skipSave.emit();
    }
  }

  onCancel(): void {
    this.cancelled.emit();
    this.close();
  }

  private close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
